import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resourcesAPI } from "api";
import { Paging, Tab } from "api/types";
import { RootState } from "app/root-reducer";
import { RejectWithMessage } from "features/types";
import { getErrorMessage } from "utils/error";
import { ResourceNetworksModerationState, ResourceNetworksModerationTableResponse } from "./types";
import { PostNetworksModerationTabs, GetPostNetworksModerationTableFilters, PostNetworkSharingStatus } from 'features/post-networks-moderation/types';

export const getResourceNetworksModerationTabs = createAsyncThunk<Tab[], void, RejectWithMessage>(
  'resources-networks-moderation/get-tabs',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.resourceNetworksModeration.currentFilters;
      return await resourcesAPI.getResourceNetworksModerationTabs(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getResourceNetworksModerationTable = createAsyncThunk<ResourceNetworksModerationTableResponse, void, RejectWithMessage>(
  'resources-networks-moderation/get-table',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = { ...state.resourceNetworksModeration.requestData, filters: state.resourceNetworksModeration.currentFilters };
      return await resourcesAPI.getResourceNetworksModerationTable(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState: ResourceNetworksModerationState = {
  status: 'idle',
  currentFilters: { tab: PostNetworksModerationTabs.Pending },
  requestData: {
    paging: {
      page: 1,
      pageSize: 20,
    },
  },
  data: {
    tabsIsLoading: false,
    tabs: [],
    tableDataIsLoading: false,
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
  },
  error: null,
};

const resourceNetworksModerationSlice = createSlice({
  name: 'resourceNetworksModeration',
  initialState,
  reducers: {
    resetRequestData: (state) => {
      state.requestData = { ...initialState.requestData };
    },
    setFilters: (state, action: PayloadAction<Partial<GetPostNetworksModerationTableFilters>>) => {
      const newFilters = { ...state.currentFilters, ...action.payload };
      state.currentFilters = newFilters;
    },
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setTab: (state, action: PayloadAction<string>) => {
      state.currentFilters.tab = action.payload;
    },
    resetTableData: (state) => {
      state.data.tableData = initialState.data.tableData;
    },
    resetState: () => initialState,
    locallyUpdatePostStatus: (state, action: PayloadAction<{ postId: string; networkProgramId: string; status: PostNetworkSharingStatus }>) => {
      const { postId, networkProgramId } = action.payload;
      const rowIndex = state.data.tableData.rows.findIndex(r => r.id === postId);
      if (rowIndex === -1) return;

      const row = state.data.tableData.rows[rowIndex];
      const programs = row.networkProgramsResourceCanBeSharedTo;
      const programIndex = programs.findIndex(np => np.id === networkProgramId);
      if (programIndex === -1) return;

      programs.splice(programIndex, 1);

      if (programs.length === 0) {
        state.data.tableData.rows.splice(rowIndex, 1);
        state.data.tableData.totalCount = Math.max(0, state.data.tableData.totalCount - 1);
        state.data.tableData.noResult = state.data.tableData.rows.length === 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getResourceNetworksModerationTabs.pending, (state) => {
      state.data.tabsIsLoading = true;
    });
    builder.addCase(getResourceNetworksModerationTabs.fulfilled, (state, action: PayloadAction<Tab[]>) => {
      state.data.tabsIsLoading = false;
      state.data.tabs = action.payload;
    });
    builder.addCase(getResourceNetworksModerationTabs.rejected, (state) => {
      state.data.tabsIsLoading = false;
    });

    builder.addCase(getResourceNetworksModerationTable.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getResourceNetworksModerationTable.fulfilled, (state, action: PayloadAction<ResourceNetworksModerationTableResponse>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getResourceNetworksModerationTable.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });
  },
});

export const { resetRequestData, setFilters, setPage, setTab, resetTableData, resetState, locallyUpdatePostStatus } = resourceNetworksModerationSlice.actions;
export const resourceNetworksModerationReducer = resourceNetworksModerationSlice.reducer;
